import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, ChevronRight } from 'lucide-react';

const VIDEOS = [
  '/videos/city.mp4',      // cidade aérea noturna
  '/videos/logistics3.mp4', // logística / indústria
];

const INTERVAL = 10000;

const Hero: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const refs = useRef<(HTMLVideoElement | null)[]>([]);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const startTimer = () => {
    timer.current = setInterval(() => {
      setCurrent(c => (c + 1) % VIDEOS.length);
    }, INTERVAL);
  };

  useEffect(() => {
    refs.current[0]?.play().catch(() => {});
    startTimer();
    return () => { if (timer.current) clearInterval(timer.current); };
  }, []);

  useEffect(() => {
    refs.current.forEach((v, i) => {
      if (!v) return;
      if (i === current) {
        v.play().catch(() => {});
      } else {
        v.pause();
        v.currentTime = 0;
      }
    });
  }, [current]);

  return (
    <section id="home" style={s.section}>

      {/* ── Video background ── */}
      <div style={s.videoWrap}>
        {VIDEOS.map((src, i) => (
          <video
            key={i}
            ref={el => { refs.current[i] = el; }}
            src={src}
            muted
            loop
            playsInline
            preload="auto"
            style={{
              ...s.video,
              opacity: i === current ? 1 : 0,
              transition: 'opacity 1.8s ease',
            }}
          />
        ))}
        <div style={s.overlay} />
        <div style={s.gradBottom} />
      </div>

      {/* ── Grid pattern ── */}
      <div style={s.grid} />

      {/* ── Main content ── */}
      <div style={s.content}>
        <motion.h1
          style={s.title}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
        >
          Tecnologia que{' '}
          <span style={s.titleAccent}>Transforma</span>
          <br />o Futuro
        </motion.h1>

        <motion.p
          style={s.subtitle}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          Soluções tecnológicas inovadoras para indústrias inteligentes,<br />
          transformação digital e cidades do futuro.
        </motion.p>

        <motion.div
          style={s.actions}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
        >
          <button
            style={s.btnPrimary}
            onClick={() => document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Explorar Serviços
            <ChevronRight size={18} />
          </button>
          <button
            style={s.btnOutline}
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Fale Connosco
          </button>
        </motion.div>

        {/* Stats */}
        <motion.div
          style={s.stats}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.7 }}
        >
          {[
            { val: '3', label: 'Áreas de Especialização' },
            { val: '18+', label: 'Soluções Implementadas' },
            { val: '100%', label: 'Foco em Inovação' },
          ].map((st, i) => (
            <div key={i} style={s.statItem}>
              <span style={s.statVal}>{st.val}</span>
              <span style={s.statLabel}>{st.label}</span>
            </div>
          ))}
        </motion.div>
      </div>

      {/* ── Dots ── */}
      <div style={s.dots}>
        {VIDEOS.map((_, i) => (
          <button
            key={i}
            onClick={() => {
              if (timer.current) clearInterval(timer.current);
              setCurrent(i);
              startTimer();
            }}
            style={{ ...s.dot, ...(i === current ? s.dotActive : {}) }}
          />
        ))}
      </div>

      {/* ── Scroll indicator ── */}
      <motion.button
        style={s.scrollBtn}
        onClick={() => document.querySelector('#about')?.scrollIntoView({ behavior: 'smooth' })}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown size={20} color="rgba(255,255,255,0.6)" />
        </motion.div>
      </motion.button>
    </section>
  );
};

const s: { [key: string]: React.CSSProperties } = {
  section: {
    position: 'relative',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    background: '#020F1E',
  },
  videoWrap: {
    position: 'absolute',
    inset: 0,
  },
  video: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    willChange: 'opacity',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, rgba(2,15,30,0.80) 0%, rgba(2,15,30,0.50) 50%, rgba(0,20,40,0.70) 100%)',
  },
  gradBottom: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '55%',
    background: 'linear-gradient(to top, #020F1E 0%, rgba(2,15,30,0.6) 50%, transparent 100%)',
  },
  grid: {
    position: 'absolute',
    inset: 0,
    backgroundImage: `
      linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)
    `,
    backgroundSize: '60px 60px',
    zIndex: 1,
    pointerEvents: 'none',
  },
  content: {
    position: 'relative',
    zIndex: 2,
    maxWidth: '900px',
    margin: '0 auto',
    padding: '0 2rem',
    textAlign: 'center',
    paddingTop: '85px',
  },
  title: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 'clamp(3rem, 8vw, 6.5rem)',
    fontWeight: 900,
    color: '#ffffff',
    lineHeight: 1.05,
    marginBottom: '1.75rem',
    letterSpacing: '-0.02em',
    textShadow: '0 2px 40px rgba(0,0,0,0.4)',
  },
  titleAccent: {
    background: 'linear-gradient(135deg, #0057FF 0%, #00C896 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  subtitle: {
    fontSize: 'clamp(1rem, 2vw, 1.2rem)',
    color: 'rgba(255,255,255,0.65)',
    lineHeight: 1.75,
    fontWeight: 400,
    maxWidth: '600px',
    margin: '0 auto 2.5rem',
    textShadow: '0 1px 12px rgba(0,0,0,0.5)',
  },
  actions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginBottom: '4rem',
  },
  btnPrimary: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.9rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    color: 'white',
    background: '#0057FF',
    padding: '1rem 2rem',
    border: 'none',
    cursor: 'pointer',
  },
  btnOutline: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.9rem',
    fontWeight: 600,
    letterSpacing: '0.05em',
    color: 'rgba(255,255,255,0.85)',
    background: 'rgba(255,255,255,0.08)',
    padding: '1rem 2rem',
    border: '1px solid rgba(255,255,255,0.25)',
    cursor: 'pointer',
    backdropFilter: 'blur(6px)',
  },
  stats: {
    display: 'flex',
    gap: '3rem',
    justifyContent: 'center',
    flexWrap: 'wrap',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    paddingTop: '2rem',
    marginTop: '1rem',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.25rem',
  },
  statVal: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '2.5rem',
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.02em',
  },
  statLabel: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: 'rgba(255,255,255,0.45)',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  dots: {
    position: 'absolute',
    bottom: '5rem',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '0.6rem',
    zIndex: 3,
  },
  dot: {
    width: '28px',
    height: '3px',
    background: 'rgba(255,255,255,0.25)',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    transition: 'all 0.3s',
  },
  dotActive: {
    background: '#0057FF',
    width: '48px',
  },
  scrollBtn: {
    position: 'absolute',
    bottom: '2rem',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'none',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '50%',
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 3,
  },
};

export default Hero;
